/**
 * Mood Animation Applicator
 * Applies mood states to avatar with ADDITIVE blending (true overlay on other animations)
 * Moods blend with lip-sync, idle, and other animations instead of replacing them
 */

import * as THREE from 'three';
import { MOOD_STATES } from '../animations/moodStates';
import type { Sentiment } from './sentimentAnalyzer';
import { resolveMorphTargetIndex } from '../converter';

let activeMoodOverlay: { stop: () => void; mood: Sentiment } | null = null;
let moodOverrides: Record<string, { blendshapes?: Record<string, number>; intensity?: number; enabled?: boolean }> = {};
let moodIntensityScale = 1;

/**
 * Set per-model mood overrides that merge on top of the hardcoded MOOD_STATES.
 * Called once when the avatar loads its resolved mood config from the schema-service.
 */
export function setMoodOverrides(overrides: Record<string, { blendshapes?: Record<string, number>; intensity?: number; enabled?: boolean }>) {
  moodOverrides = overrides ?? {};
  console.log('🎭 Mood overrides applied:', Object.keys(moodOverrides).length, 'moods');
}

/**
 * Set the per-model mood-intensity multiplier. Applied at overlay time as a
 * multiplier on each mood's `intensity` field. Sam ships with 0.55 because
 * the legacy default mesh exaggerates frown blendshapes; other models default
 * to 1 (no change). Sourced from ModelConfig.moodIntensity in modelRegistry.
 *
 * Called from Avatar.tsx when the active model is resolved. Pass 1 (or omit)
 * to reset; pass undefined to leave the previous value in place.
 *
 * Slice 6 of the avatar-fixes plan, May 11 2026.
 */
export function setMoodIntensityScale(scale: number | undefined) {
  if (typeof scale !== 'number' || !Number.isFinite(scale)) {
    return;
  }
  const next = clamp01(scale);
  if (next === moodIntensityScale) {
    return;
  }
  moodIntensityScale = next;
  console.log('🎭 Mood intensity scale set:', moodIntensityScale);
}

/** Read-only accessor used by tests + Avatar Editor preview. */
export function getMoodIntensityScale(): number {
  return moodIntensityScale;
}

function getResolvedMoodState(mood: Sentiment) {
  const base = MOOD_STATES[mood];
  if (!base) return null;
  const override = moodOverrides[mood];
  if (!override) return base;
  return {
    ...base,
    blendshapes: { ...base.blendshapes, ...(override.blendshapes ?? {}) },
    intensity: override.intensity ?? base.intensity,
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function easeOutCubic(t: number): number {
  const normalized = clamp01(t);
  return 1 - Math.pow(1 - normalized, 3);
}

function getMixerRoot(mixer: THREE.AnimationMixer): THREE.Object3D | null {
  const maybeGetRoot = (mixer as unknown as { getRoot?: () => THREE.Object3D }).getRoot;
  if (typeof maybeGetRoot === 'function') {
    return maybeGetRoot.call(mixer);
  }
  return ((mixer as unknown as { _root?: THREE.Object3D })._root) ?? null;
}

function isGenericBodyMorphKey(key: string): boolean {
  return /^body_morph_\d+$/i.test(key) || /^target_\d+$/i.test(key);
}

function hasNamedFacialMorphs(keys: string[]): boolean {
  return keys.some((key) => {
    const normalized = key.toLowerCase();
    return normalized.includes('jaw') ||
      normalized.includes('mouth') ||
      normalized.includes('tongue') ||
      normalized.includes('cheek') ||
      normalized.includes('brow') ||
      normalized.includes('eye') ||
      normalized.includes('nose');
  });
}

function isMoodCandidateMesh(mesh: any, bodyMeshName: string | undefined): boolean {
  if (!mesh?.morphTargetInfluences || !mesh?.morphTargetDictionary) {
    return false;
  }

  const morphKeys = Object.keys(mesh.morphTargetDictionary);
  if (morphKeys.length === 0) {
    return false;
  }

  if (bodyMeshName && mesh.name === bodyMeshName) {
    return true;
  }

  if (hasNamedFacialMorphs(morphKeys)) {
    return true;
  }

  const meshName = String(mesh.name || '').toLowerCase();
  const isFaceNamedMesh = meshName.includes('head') || meshName.includes('face') || meshName.includes('mouth');
  const hasOnlyGenericBodyMorphs = morphKeys.every(isGenericBodyMorphKey);

  return isFaceNamedMesh && !hasOnlyGenericBodyMorphs;
}

function collectMoodMeshes(
  mixer: THREE.AnimationMixer,
  blendshapes: Record<string, number>,
  bodyMeshName?: string
): Array<{
  mesh: THREE.Mesh & { morphTargetInfluences: number[]; morphTargetDictionary: Record<string, number> };
  targets: Array<[number, number]>;
}> {
  const root = getMixerRoot(mixer);
  if (!root) {
    return [];
  }

  const moodMeshes: Array<{
    mesh: THREE.Mesh & { morphTargetInfluences: number[]; morphTargetDictionary: Record<string, number> };
    targets: Array<[number, number]>;
  }> = [];

  root.traverse((node: any) => {
    if (!isMoodCandidateMesh(node, bodyMeshName)) {
      return;
    }

    const targets: Array<[number, number]> = [];
    for (const [key, value] of Object.entries(blendshapes)) {
      const idx = resolveMorphTargetIndex(node.morphTargetDictionary, key);
      if (idx !== undefined && idx < node.morphTargetInfluences.length) {
        targets.push([idx, clamp01(Number(value) || 0)]);
      }
    }

    if (targets.length > 0) {
      moodMeshes.push({ mesh: node, targets });
    }
  });

  return moodMeshes;
}

function startMoodOverlay(
  mood: Sentiment,
  mixer: THREE.AnimationMixer,
  blendshapes: Record<string, number>,
  transitionDuration: number,
  bodyMeshName?: string,
  intensity = 1
): { stop: () => void; mood: Sentiment } | null {
  const moodMeshes = collectMoodMeshes(mixer, blendshapes, bodyMeshName);
  if (moodMeshes.length === 0) {
    console.warn('[moodAnimator] No compatible morph mesh found for mood:', mood);
    return null;
  }

  let rafId: number | null = null;
  let disposed = false;
  const startTime = performance.now();
  const durationMs = Math.max(80, transitionDuration || 400);
  const appliedValues = new WeakMap<THREE.Mesh, Map<number, number>>();

  const applyAtWeight = (weight: number) => {
    const effectiveWeight = clamp01(weight) * clamp01(intensity) * clamp01(moodIntensityScale);

    for (const { mesh, targets } of moodMeshes) {
      const influences = mesh.morphTargetInfluences;
      if (!influences) {
        continue;
      }

      let appliedForMesh = appliedValues.get(mesh);
      if (!appliedForMesh) {
        appliedForMesh = new Map<number, number>();
        appliedValues.set(mesh, appliedForMesh);
      }

      for (const [idx, target] of targets) {
        const previousOverlay = appliedForMesh.get(idx) ?? 0;
        const nextOverlay = target * effectiveWeight;
        influences[idx] = clamp01((influences[idx] ?? 0) - previousOverlay + nextOverlay);
        if (nextOverlay < 0.001) {
          appliedForMesh.delete(idx);
        } else {
          appliedForMesh.set(idx, nextOverlay);
        }
      }
    }
  };

  const clearOverlay = () => {
    for (const { mesh } of moodMeshes) {
      const influences = mesh.morphTargetInfluences;
      const appliedForMesh = appliedValues.get(mesh);
      if (!influences || !appliedForMesh) {
        continue;
      }

      appliedForMesh.forEach((value, idx) => {
        influences[idx] = clamp01((influences[idx] ?? 0) - value);
      });
      appliedForMesh.clear();
    }
  };

  const animate = (now: number) => {
    if (disposed) {
      return;
    }

    const progress = easeOutCubic((now - startTime) / durationMs);
    applyAtWeight(progress);
    rafId = requestAnimationFrame(animate);
  };

  rafId = requestAnimationFrame(animate);

  return {
    mood,
    stop() {
      disposed = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      clearOverlay();
    }
  };
}

export async function applyMoodToAvatar(
  _avatarRef: any,
  mood: Sentiment,
  currentMood: Sentiment,
  transitionDuration: number,
  morphTargetDictionary: any,
  mixer: THREE.AnimationMixer,
  bodyMeshName?: string
): Promise<void> {
  
  if (!morphTargetDictionary || !mixer) {
    console.warn('Cannot apply mood - missing dependencies');
    return;
  }

  const moodState = getResolvedMoodState(mood);
  if (!moodState) {
    console.warn('Unknown mood state:', mood);
    return;
  }

  try {
    activeMoodOverlay?.stop();
    activeMoodOverlay = startMoodOverlay(
      mood,
      mixer,
      moodState.blendshapes,
      transitionDuration,
      bodyMeshName,
      (moodState as unknown as { intensity?: number }).intensity ?? 1
    );

    if (activeMoodOverlay) {
      console.log('Mood overlay playing:', mood, 'on mesh:', bodyMeshName || '(auto)');
    }
  } catch (error) {
    console.error('Error applying mood:', error);
  }
}

/**
 * Get current mood intensity based on conversation context
 * @param recentSentiments - Array of recent sentiment detections
 * @returns Intensity multiplier (0-1)
 */
export function calculateMoodIntensity(recentSentiments: Array<{ sentiment: Sentiment; confidence: number }>): number {
  if (recentSentiments.length === 0) return 0.5;

  // Average confidence of recent sentiments
  const avgConfidence = recentSentiments.reduce((sum, s) => sum + s.confidence, 0) / recentSentiments.length;

  // Higher confidence = stronger mood expression
  return Math.min(avgConfidence * 1.2, 1.0);
}

